import { apiClient, handleApiError } from './api'

export const reviewsService = {
  // Get all reviews with pagination and filters
  async getReviews(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      // Add pagination params
      if (params.page) queryParams.append('page', params.page)
      if (params.limit) queryParams.append('per_page', params.limit) // Backend expects per_page
      
      // Add filter params
      if (params.rating) queryParams.append('star_rating', params.rating) // Backend expects star_rating
      if (params.location_id) queryParams.append('place_id', params.location_id) // Backend expects place_id
      if (params.place_id) queryParams.append('place_id', params.place_id)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      if (params.search) queryParams.append('search_text', params.search) // Backend expects search_text
      if (params.status) queryParams.append('status', params.status)
      // Add has_reply parameter - THIS WAS MISSING!
      if (params.has_reply === true || params.has_reply === false) {
        queryParams.append('has_reply', params.has_reply.toString())
        console.log('✅ Added has_reply filter:', params.has_reply)
      }
      // Add sorting params
      if (params.sort_by) queryParams.append('sort_by', params.sort_by)
      if (params.sort_order) queryParams.append('sort_order', params.sort_order)
      
      const url = `/api/reviews${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get single review by ID
  async getReview(reviewId) {
    try {
      const response = await apiClient.get(`/api/reviews/${reviewId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Reply to a review
  async replyToReview(reviewId, replyText) {
    try {
      const response = await apiClient.post(`/api/reviews/${reviewId}/reply`, {
        reply_text: replyText
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Generate AI reply for a review
  async generateAIReply(reviewId, options = {}) {
    try {
      const { tone = 'professional', custom_instructions = null } = options
      const requestBody = { tone }
      
      if (custom_instructions) {
        requestBody.custom_instructions = custom_instructions
      }
      
      const response = await apiClient.post(`/api/reviews/${reviewId}/generate-reply`, requestBody)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update review status (read/unread, flagged, etc.)
  async updateReviewStatus(reviewId, status) {
    try {
      const response = await apiClient.patch(`/api/reviews/${reviewId}/status`, {
        status
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Bulk update review statuses
  async bulkUpdateReviews(reviewIds, updates) {
    try {
      const response = await apiClient.patch('/api/reviews/bulk-update', {
        review_ids: reviewIds,
        updates
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get review analytics
  async getReviewAnalytics(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      if (params.period) queryParams.append('period', params.period)
      
      const url = `/api/reviews/analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get rating trends
  async getRatingTrends(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      // Add required place_id parameter - use default business location if not specified
      const placeId = params.place_id || 'ChIJ_7oK9lzzxokR18THkBwGoXQ'
      queryParams.append('place_id', placeId)
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.period) queryParams.append('period', params.period)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      
      const url = `/api/reviews/trends${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get review summary stats
  async getReviewStats(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      // Add required place_id parameter - use default business location if not specified
      const placeId = params.place_id || 'ChIJ_7oK9lzzxokR18THkBwGoXQ'
      queryParams.append('place_id', placeId)
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      if (params.period) queryParams.append('period', params.period)
      
      const url = `/api/reviews/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get review insights and sentiment analysis
  async getReviewInsights(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.period) queryParams.append('period', params.period)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      
      const url = `/api/reviews/insights${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get competitor analysis
  async getCompetitorAnalysis(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.competitors) queryParams.append('competitors', JSON.stringify(params.competitors))
      if (params.period) queryParams.append('period', params.period)
      
      const url = `/api/reviews/competitor-analysis${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get review response suggestions
  async getResponseSuggestions(reviewId, tone = 'professional') {
    try {
      const response = await apiClient.post(`/api/reviews/${reviewId}/suggestions`, {
        tone
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Sync reviews from Google Business Profile
  async syncReviews(locationId) {
    try {
      const response = await apiClient.post('/api/reviews/sync', {
        location_id: locationId
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Bulk sync all locations
  async syncAllReviews() {
    try {
      const response = await apiClient.post('/api/reviews/sync-all')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get sync status
  async getSyncStatus() {
    try {
      const response = await apiClient.get('/api/reviews/sync-status')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Export reviews data
  async exportReviews(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      if (params.format) queryParams.append('format', params.format)
      if (params.include_replies) queryParams.append('include_replies', params.include_replies)
      
      const url = `/api/reviews/export${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url, {
        responseType: 'blob'
      })
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get review templates
  async getReviewTemplates() {
    try {
      const response = await apiClient.get('/api/reviews/templates')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Create review template
  async createReviewTemplate(template) {
    try {
      const response = await apiClient.post('/api/reviews/templates', template)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update review template
  async updateReviewTemplate(templateId, updates) {
    try {
      const response = await apiClient.patch(`/api/reviews/templates/${templateId}`, updates)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Delete review template
  async deleteReviewTemplate(templateId) {
    try {
      const response = await apiClient.delete(`/api/reviews/templates/${templateId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get review notifications
  async getReviewNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.page) queryParams.append('page', params.page)
      if (params.limit) queryParams.append('limit', params.limit)
      if (params.type) queryParams.append('type', params.type)
      if (params.read) queryParams.append('read', params.read)
      
      const url = `/api/reviews/notifications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Mark notification as read
  async markNotificationRead(notificationId) {
    try {
      const response = await apiClient.patch(`/api/reviews/notifications/${notificationId}/read`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Mark all notifications as read
  async markAllNotificationsRead() {
    try {
      const response = await apiClient.patch('/api/reviews/notifications/read-all')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get review performance metrics
  async getPerformanceMetrics(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.period) queryParams.append('period', params.period)
      if (params.metric_type) queryParams.append('metric_type', params.metric_type)
      
      const url = `/api/reviews/performance${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Schedule review report
  async scheduleReport(reportConfig) {
    try {
      const response = await apiClient.post('/api/reviews/schedule-report', reportConfig)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get scheduled reports
  async getScheduledReports() {
    try {
      const response = await apiClient.get('/api/reviews/scheduled-reports')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Delete scheduled report
  async deleteScheduledReport(reportId) {
    try {
      const response = await apiClient.delete(`/api/reviews/scheduled-reports/${reportId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }
}

export default reviewsService
